import React, { useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { getPageContainerStyles } from '../utils/styleUtils';
import { getThemeForPage } from '../styles/themes';
import { useLocation } from 'react-router-dom';
import styles from './GuidePage.module.css';

function getLocale() {
  const lang = (navigator.language || navigator.userLanguage || '').toLowerCase();
  if (lang.startsWith('fr')) return 'fr';
  if (lang.startsWith('ko')) return 'ko';
  if (lang.startsWith('en')) return 'en';
  return 'ko'; // 기본값을 한국어로 설정
}

const TEXT = {
  guide: {
    ko: {
      title: '[간략 가이드]',
      calendarUsage: '캘린더 사용법',
      point1: '원하는 시간을 터치한 상태로 잠시 기다리면 박스가 생성됩니다. 그 상태에서 드래그하면 여러 시간대 구간을 설정할 수 있습니다.',
      point2: '공개 캘린더는 손님에게 본인의 스케줄을 보여줄 때 유용합니다. 현재 달부터 2개월 이후까지만 표시됩니다.',
      point3: '휴무(OFF) 모드를 통해 본인이 일하지 않는 날을 표시할 수 있습니다. 이 설정은 공개 캘린더에 즉시 반영되어, 손님은 해당 날짜를 볼 수 없게 됩니다.',
      point4: '일정을 추가할 때 예약정보를 입력할 수 있습니다. 등록된 예약정보는 메인 하단 메뉴의 [예약] 페이지에서 표 형식으로 확인할 수 있으며, 수정·삭제가 가능합니다. 또한 이미지 업로드도 지원됩니다.'
    },
    fr: {
      title: '[Guide rapide]',
      calendarUsage: 'Utilisation du calendrier',
      point1: 'Touchez et maintenez l\'écran à l\'endroit souhaité, un bloc apparaîtra. En le déplaçant, vous pouvez sélectionner plusieurs créneaux horaires.',
      point2: 'Le calendrier public est utile pour montrer votre planning aux clients. Il affiche uniquement le mois en cours et les deux mois suivants.',
      point3: 'Le mode OFF (repos) permet de marquer vos jours non travaillés. Cette information est immédiatement reflétée dans le calendrier public : vos clients ne verront donc pas ces dates.',
      point4: 'Lors de l\'ajout d\'un événement, vous pouvez renseigner des informations de réservation. Celles-ci sont regroupées dans le menu principal, section [Réservations], sous forme de tableau. Vous pouvez les modifier ou les supprimer, et il est également possible de téléverser des images.'
    },
    en: {
      title: '[Quick Guide]',
      calendarUsage: 'Calendar usage',
      point1: 'Tap and hold on the desired time slot; a box will appear. By dragging, you can select multiple time ranges.',
      point2: 'The public calendar is useful for showing your schedule to clients. It displays only the current month and the next two months.',
      point3: 'The OFF mode allows you to mark your non-working days. This is instantly reflected in the public calendar, so clients will not see those dates.',
      point4: 'When adding an event, you can include booking details. These bookings are listed in the main menu under [Bookings], where you can view them in a table, as well as edit or delete them. Image uploads are also supported.'
    }
  }
};

function GuidePage() {
  const { userInfo } = useContext(UserContext);
  const locale = userInfo?.preferred_language || getLocale();
  const location = useLocation();

  // 공통 스타일 가져오기
  const pageContainerStyles = getPageContainerStyles();

  // 테마 설정
  const currentTheme = getThemeForPage(location.pathname);

  // 사용자가 선택한 테마 확인
  const userSelectedTheme = localStorage.getItem('user_theme') || 'modern';
  const isDosTheme = userSelectedTheme === 'dos';

  return (
    <div
      className={pageContainerStyles.pageContainer}
      style={{
        overflow: 'auto',
        height: '100%',
        backgroundColor: currentTheme.backgroundColor,
        '--active-color': currentTheme.activeColor,
        '--inactive-color': currentTheme.inactiveColor,
        '--background-color': currentTheme.backgroundColor,
        '--border-color': currentTheme.borderColor
      }}
    >
      {/* 페이지 타이틀 */}
      <h1 className={pageContainerStyles.pageTitle}>
        {TEXT.guide[locale].title}
      </h1>

      {/* 가이드 컨텐츠 */}
      <div style={{
        border: isDosTheme ? 'none' : '1px solid #666666',
        background: isDosTheme ? '#151515' : 'transparent',
        padding: '20px',
        borderRadius: isDosTheme ? '0' : '8px',
        marginBottom: '20px',
        color: isDosTheme ? '#f0f0f0' : '#333333',
        '--active-color': isDosTheme ? '#00ff00' : '#c00d0d',
        '--inactive-color': isDosTheme ? '#f0f0f0' : '#333333',
        '--border-color': isDosTheme ? 'transparent' : '#666666'
      }}>
        <div className={styles['guide-content']}>
          <h3 className={styles['guide-subtitle']}>{TEXT.guide[locale].calendarUsage}</h3>
          <ul className={styles['guide-list']}>
            <li>{TEXT.guide[locale].point1}</li>
            <li>{TEXT.guide[locale].point2}</li>
            <li>{TEXT.guide[locale].point3}</li>
            <li>{TEXT.guide[locale].point4}</li>
          </ul>
        </div>
      </div>

      {/* 최하단 여백 */}
      <div style={{ height: 50 }} />
    </div>
  );
}

export default GuidePage;
